import { ThunkDispatch } from '@reduxjs/toolkit';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { AppState } from '../../../stores/redux-store';
import {
  createReportStart,
  createReportSuccess,
  fetchAccountsSuccess,
} from '../../../stores/account-check-store';
import { setApplicationError } from '../../../stores/app-state-store';
import { ajaxRetry } from '../../../lib-api/ajaxRetry';
import { base, createApiURL, ResponseObj } from '../../../lib-api/services/base';
import {
  AccountCheckReportEntity,
  CheckAccountEntity,
  CheckAccountsEntity,
} from '../models/accountCheck';
import { ApplicationError } from '../../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { UNEXPECTED_ERROR } from '../../../components/ApplicationErrorBoundary/ApplicationErrorBoundary';

export const getAccounts = (): Promise<ResponseObj<CheckAccountsEntity>> => {
  return ajaxRetry<CheckAccountsEntity>({
    headers: base.headers(),
    method: 'GET',
    url: createApiURL(`/api/v1/accounts/list`),
  });
};

export const createReport = (
  includedAccounts: string[]
): Promise<ResponseObj<AccountCheckReportEntity>> => {
  return ajaxRetry<AccountCheckReportEntity>({
    data: JSON.stringify({ includedAccounts }),
    headers: base.headers(),
    method: 'POST',
    url: createApiURL(`/api/v1/account-verification-reports`),
  });
};

export interface AccountCheckClient {
  getAccounts: () => Promise<ResponseObj<CheckAccountsEntity>>;
  createReport: (includedAccounts: string[]) => Promise<ResponseObj<AccountCheckReportEntity>>;
}

export type GetAccountsDispatch = ThunkDispatch<
  AppState,
  void,
  ReturnType<typeof fetchAccountsSuccess> | ReturnType<typeof setApplicationError>
>;

export type CreateReportDispatch = ThunkDispatch<
  AppState,
  void,
  | ReturnType<typeof createReportStart>
  | ReturnType<typeof createReportSuccess>
  | ReturnType<typeof setApplicationError>
>;

type GetAccountsThunk = (dispatch: GetAccountsDispatch) => Promise<void>;
type CreateReportThunk = (dispatch: CreateReportDispatch) => Promise<void>;

export interface AccountCheckService {
  getAccounts: () => Promise<CheckAccountEntity[]>;
  createReport: (includedAccounts: string[]) => Promise<string>;
}

export interface AccountCheckServiceRedux {
  getAccounts: () => GetAccountsThunk;
  createReport: (includedAccounts: string[]) => CreateReportThunk;
}

export const accountCheckClient: AccountCheckClient = {
  getAccounts,
  createReport,
};

export const ERR_ACCOUNT_CHECK_NO_ACCOUNTS = {
  errorStatus: ErrorStatus.TEMPORARY_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_ACCOUNTS,
};

export const ERR_ACCOUNT_CHECK_FAILED_GET_ACCOUNTS = ERR_ACCOUNT_CHECK_NO_ACCOUNTS;

export const ERR_ACCOUNT_CHECK_FAILED_CREATE_REPORT = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_CREATE_ACCOUNT_CHECK_REPORT,
};

export function createAccountCheckService(client: AccountCheckClient): AccountCheckService {
  return {
    getAccounts: async () => {
      const response = await client.getAccounts();

      if (response.isOk()) {
        const { accounts } = response.data;

        if (accounts.length > 0) {
          return accounts;
        }
        throw new ApplicationError(ERR_ACCOUNT_CHECK_NO_ACCOUNTS);
      } else {
        throw new ApplicationError(ERR_ACCOUNT_CHECK_FAILED_GET_ACCOUNTS);
      }
    },
    createReport: async (includedAccounts: string[]) => {
      const response = await client.createReport(includedAccounts);

      if (response.isOk()) {
        const { id } = response.data;

        return id;
      }

      throw new ApplicationError(ERR_ACCOUNT_CHECK_FAILED_CREATE_REPORT);
    },
  };
}

export const accountCheckService = createAccountCheckService(accountCheckClient);

export const createAccountCheckServiceWithDispatch = (service: AccountCheckService) => ({
  getAccounts: (): GetAccountsThunk => async (dispatch) => {
    try {
      const res = await service.getAccounts();
      dispatch(fetchAccountsSuccess(res));
    } catch (e) {
      if (e instanceof ApplicationError) {
        dispatch(setApplicationError(e.commonError));
      } else {
        dispatch(setApplicationError(UNEXPECTED_ERROR));
      }
    }
  },
  createReport:
    (accounts: string[]): CreateReportThunk =>
    async (dispatch) => {
      try {
        dispatch(createReportStart());
        const res = await service.createReport(accounts);
        dispatch(createReportSuccess(res));
      } catch (e) {
        if (e instanceof ApplicationError) {
          dispatch(setApplicationError(e.commonError));
        } else {
          dispatch(setApplicationError(UNEXPECTED_ERROR));
        }
      }
    },
});

export const accountCheckServiceRedux: AccountCheckServiceRedux =
  createAccountCheckServiceWithDispatch(accountCheckService);
